import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState, EmptyState } from "@/components/Bits";
import { Modal, Field, inputCls, primaryBtnCls, ghostBtnCls, Table, Td } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";
import { dmyTime } from "@/lib/format";

const STATUS_LABEL = {
  pending_billing: "Pending billing", ordered: "Ordered", collected: "Sample collected",
  in_progress: "In progress", completed: "Results entered", verified: "Verified",
  reported: "Report released",
};
const PAY_TONE = { unpaid: "text-rose-600", partial: "text-amber-600", paid: "text-emerald-600" };

export default function LabOrders() {
  const { can } = useAuth();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [statusF, setStatusF] = useState(params.get("status") || "");
  const [q, setQ] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setItems(null);
    try {
      const { data } = await api.get("/lab/orders", { params: { status: statusF || undefined, q: q || undefined } });
      setItems(data.items);
    } catch (e) { setError(apiError(e)); }
  }, [statusF, q]);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (o) => {
    // Open the richer New Bill workflow directly, prefilled from & linked to this order.
    // If a bill already exists for the order, jump straight to it (never duplicate).
    if (o.bill_id) { nav(`/app/lab/bills/${o.bill_id}`); return; }
    nav(`/app/lab/new-bill?order_id=${o.order_id}`);
  };

  if (!can("lab.orders.view")) return <NoAccess what="Lab orders" />;

  return (
    <div data-testid="lab-orders-page">
      <PageHeader overline="Laboratory" title="Lab Orders"
        subtitle="Order tests, collect samples, enter and verify results, then release the payment-cleared report."
        actions={can("lab.orders.create") && <button onClick={() => setOpenNew(true)} className={primaryBtnCls} data-testid="new-lab-order-btn"><Plus className="h-4 w-4" /> New order</button>} />

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Patient, order no, UHID…"
            data-testid="lab-order-search" className={`${inputCls} pl-9 max-w-xs`} />
        </div>
        <select value={statusF} onChange={(e) => { setStatusF(e.target.value); setParams(e.target.value ? { status: e.target.value } : {}); }}
          data-testid="lab-order-status-filter" className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {error && <ErrorState message={error} />}
      {!items ? <Loading /> : (
        <Table testid="lab-orders-table" columns={["Order", "Patient", "Tests", "Status", "Billing", "Report", ""]}
          rows={items} empty={<EmptyState title="No lab orders" subtitle="Create a lab order to begin the diagnostic workflow." testid="lab-orders-empty" />}
          renderRow={(o) => (
            <tr key={o.order_id} data-testid={`lab-order-row-${o.order_id}`}>
              <Td className="font-mono text-xs text-slate-700">{o.order_no}<span className="block text-slate-400">{dmyTime(o.created_at)}</span></Td>
              <Td className="font-medium text-slate-900">{o.patient_name}<span className="block text-xs text-slate-400">{o.patient_uhid}</span></Td>
              <Td className="text-slate-600 text-xs">{o.items.map((i) => i.name).join(", ")}</Td>
              <Td><span className="text-xs font-medium text-slate-700">{STATUS_LABEL[o.status] || o.status}</span></Td>
              <Td className="text-xs">₹{o.total} · <span className={PAY_TONE[o.payment_status]}>{o.payment_status}{o.due > 0 ? ` (₹${o.due} due)` : ""}</span></Td>
              <Td><span className={`text-xs font-medium ${o.report_released ? "text-emerald-600" : "text-slate-400"}`}>{o.report_released ? "Released" : "Held"}</span></Td>
              <Td><button onClick={() => openDetail(o)} className={ghostBtnCls} data-testid={`open-lab-order-${o.order_id}`}>Open</button></Td>
            </tr>
          )} />
      )}

      {openNew && <NewOrderModal onClose={() => setOpenNew(false)} onDone={() => { setOpenNew(false); load(); }} />}
      {detail && <OrderDetail order={detail} setOrder={setDetail} onClose={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function NewOrderModal({ onClose, onDone }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [patient, setPatient] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [doctorId, setDoctorId] = useState("");
  const [tests, setTests] = useState([]);
  const [selected, setSelected] = useState({});
  const [discount, setDiscount] = useState(0);
  const [tax, setTax] = useState(0);
  const [advance, setAdvance] = useState(0);
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState("");
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [d, t, p] = await Promise.all([
          api.get("/hospital/doctors"), api.get("/lab/tests", { params: { active: true } }),
          api.get("/lab/partners", { params: { active: true } }),
        ]);
        setDoctors(d.data.items || []); setTests(t.data.items || []); setPartners(p.data.items || []);
      } catch (e) { setFormError(apiError(e)); }
    })();
  }, []);

  useEffect(() => {
    if (!q) { setResults([]); return; }
    const id = setTimeout(async () => {
      try { const { data } = await api.get("/lab/patients", { params: { q } }); setResults(data.items || []); }
      catch { /* ignore */ }
    }, 250);
    return () => clearTimeout(id);
  }, [q]);

  const chosen = tests.filter((t) => selected[t.test_id]);
  const subtotal = chosen.reduce((s, t) => s + Number(t.price || 0), 0);
  const total = Math.max(subtotal - Number(discount || 0) + Number(tax || 0), 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!patient) { setFormError("Select a patient."); return; }
    if (!chosen.length) { setFormError("Select at least one test."); return; }
    setBusy(true);
    try {
      await api.post("/lab/orders", {
        patient_id: patient.patient_id, doctor_id: doctorId, test_ids: chosen.map((t) => t.test_id),
        discount: Number(discount || 0), tax: Number(tax || 0), advance: Number(advance || 0),
        source: partnerId ? "partner" : "internal", partner_id: partnerId,
      });
      toast.success("Lab order created.");
      onDone();
    } catch (e) { setFormError(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open onClose={onClose} wide title="New lab order" testid="new-lab-order-modal">
      <form onSubmit={submit} className="space-y-5">
        {!patient ? (
          <Field label="Find patient">
            <input className={inputCls} data-testid="lab-patient-search" placeholder="Search by name, UHID or phone…" value={q} onChange={(e) => setQ(e.target.value)} />
            {results.length > 0 && (
              <div className="mt-2 border border-slate-200 rounded-lg divide-y max-h-52 overflow-auto">
                {results.map((p) => (
                  <button type="button" key={p.patient_id} onClick={() => { setPatient(p); setResults([]); setQ(""); }}
                    className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm" data-testid={`lab-pick-patient-${p.patient_id}`}>
                    {p.name} <span className="text-slate-400">· {p.uhid} · {p.phone}</span>
                  </button>
                ))}
              </div>
            )}
          </Field>
        ) : (
          <div className="flex items-center justify-between rounded-lg bg-slate-50 px-4 py-3" data-testid="lab-selected-patient">
            <div><p className="font-medium text-slate-900">{patient.name}</p><p className="text-xs text-slate-500">{patient.uhid} · {patient.phone}</p></div>
            <button type="button" onClick={() => setPatient(null)} className={ghostBtnCls}>Change</button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Referring doctor (optional)">
            <select className={inputCls} data-testid="lab-doctor-select" value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
              <option value="">— None —</option>
              {doctors.map((d) => <option key={d.doctor_id} value={d.doctor_id}>Dr. {d.name}</option>)}
            </select>
          </Field>
          <Field label="B2B diagnostic partner (optional)">
            <select className={inputCls} data-testid="lab-partner-select" value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
              <option value="">— In-house —</option>
              {partners.map((p) => <option key={p.partner_id} value={p.partner_id}>{p.name} ({p.partner_share_percent}%)</option>)}
            </select>
          </Field>
        </div>

        <Field label="Tests">
          <div className="border border-slate-200 rounded-lg divide-y max-h-56 overflow-auto">
            {tests.length === 0 && <p className="px-3 py-3 text-sm text-slate-400">No active tests. Add tests in the catalogue first.</p>}
            {tests.map((t) => (
              <label key={t.test_id} className="flex items-center justify-between px-3 py-2 text-sm cursor-pointer hover:bg-slate-50" data-testid={`lab-test-option-${t.test_id}`}>
                <span className="flex items-center gap-2">
                  <input type="checkbox" checked={!!selected[t.test_id]} onChange={(e) => setSelected({ ...selected, [t.test_id]: e.target.checked })} />
                  {t.name} <span className="text-slate-400">· {t.category || "—"}</span>
                </span>
                <span className="text-slate-600">₹{t.price}</span>
              </label>
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Field label="Discount (₹)"><input type="number" className={inputCls} data-testid="lab-discount" value={discount} onChange={(e) => setDiscount(e.target.value)} /></Field>
          <Field label="Tax (₹)"><input type="number" className={inputCls} data-testid="lab-tax" value={tax} onChange={(e) => setTax(e.target.value)} /></Field>
          <Field label="Advance (₹)"><input type="number" className={inputCls} data-testid="lab-advance" value={advance} onChange={(e) => setAdvance(e.target.value)} /></Field>
          <div className="flex flex-col justify-end"><p className="text-xs text-slate-500">Total</p><p className="text-lg font-semibold text-slate-900" data-testid="lab-order-total">₹{total.toFixed(2)}</p></div>
        </div>

        {formError && <p className="text-sm text-red-600" data-testid="lab-order-error">{formError}</p>}
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onClose} className={ghostBtnCls}>Cancel</button>
          <button disabled={busy} className={primaryBtnCls} data-testid="create-lab-order-btn">{busy ? "Creating…" : "Create order"}</button>
        </div>
      </form>
    </Modal>
  );
}

function OrderDetail({ order, setOrder, onClose }) {
  const { can } = useAuth();
  const pending = order.status === "pending_billing";
  const [bill, setBill] = useState({ discount: 0, tax: 0 });
  const [results, setResults] = useState(() => Object.fromEntries(order.items.map((i) => [i.test_id, {
    result_value: i.result_value || "", unit: i.unit || "", ref_range: i.ref_range || "", flag: i.flag || "", remarks: i.remarks || "",
  }])));
  const [pay, setPay] = useState({ mode: "cash", amount: "", reference: "" });
  // Stable idempotency key per payment attempt — reused on retry so a double
  // submit / timeout-retry cannot double-charge. Fresh key after each success.
  const [payKey, setPayKey] = useState(() => `lab-order-${order.order_id}-${Date.now()}`);
  const [busy, setBusy] = useState(false);
  const locked = order.status === "verified" || order.status === "reported";

  const refresh = async () => { const { data } = await api.get(`/lab/orders/${order.order_id}`); setOrder(data.order); };
  const act = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); if (okMsg) toast.success(okMsg); await refresh(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const confirmBilling = () => act(() => api.post(`/lab/orders/${order.order_id}/confirm-billing`, {
    discount: Number(bill.discount || 0), tax: Number(bill.tax || 0) }), "Invoice created — billing confirmed.");
  const addPayment = () => act(async () => {
    if (!pay.amount || Number(pay.amount) <= 0) throw { response: { data: { detail: "Enter a valid amount." } } };
    await api.post(`/lab/orders/${order.order_id}/payment`, { mode: pay.mode, amount: Number(pay.amount), reference: pay.reference, idempotency_key: payKey });
    setPay({ mode: "cash", amount: "", reference: "" });
    setPayKey(`lab-order-${order.order_id}-${Date.now()}`);
  }, "Payment recorded.");
  const collect = () => act(() => api.post(`/lab/orders/${order.order_id}/collect`, { remarks: "" }), "Sample marked collected.");
  const saveResults = (submit) => act(() => api.put(`/lab/orders/${order.order_id}/results`, {
    submit, items: order.items.map((i) => ({ test_id: i.test_id, ...results[i.test_id] })),
  }), submit ? "Results submitted for verification." : "Results saved.");
  const verify = () => act(() => api.post(`/lab/orders/${order.order_id}/verify`), "Results verified.");
  const release = () => act(() => api.post(`/lab/orders/${order.order_id}/release`), "Report released.");
  const printReport = async () => {
    try {
      const { data } = await api.get(`/lab/orders/${order.order_id}/report`);
      openPrint(data.order, data.business, data.report_meta);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Modal open onClose={onClose} wide title={`Order ${order.order_no}`} subtitle={`${order.patient_name} · ${order.patient_uhid}${order.origin === "doctor" ? ` · from ${(order.encounter_type || "").toUpperCase()} consultation` : ""}`} testid="lab-order-detail">
      <div className="space-y-6">
        {pending && (
          <section className="panel p-4 border-amber-200 bg-amber-50/40" data-testid="lab-detail-pending-billing">
            <p className="overline text-amber-600 mb-2">Pending billing</p>
            <p className="text-sm text-slate-700 mb-3">This lab order was placed by the doctor. Review the tests below, create the invoice, then collect payment. Sample collection unlocks after billing.</p>
            <div className="border border-slate-200 rounded-lg divide-y mb-3">
              {order.items.map((i) => (
                <div key={i.test_id} className="flex justify-between px-3 py-2 text-sm"><span>{i.name}</span><span className="text-slate-600">₹{i.price}</span></div>
              ))}
              <div className="flex justify-between px-3 py-2 text-sm font-semibold"><span>Subtotal</span><span>₹{order.subtotal}</span></div>
            </div>
            {can("lab.billing.manage") ? (
              <div className="flex flex-wrap items-end gap-3">
                <div><label className="text-xs text-slate-500 block mb-1">Discount (₹)</label><input type="number" className={`${inputCls} max-w-[120px]`} value={bill.discount} onChange={(e) => setBill({ ...bill, discount: e.target.value })} data-testid="lab-confirm-discount" /></div>
                <div><label className="text-xs text-slate-500 block mb-1">Tax (₹)</label><input type="number" className={`${inputCls} max-w-[120px]`} value={bill.tax} onChange={(e) => setBill({ ...bill, tax: e.target.value })} data-testid="lab-confirm-tax" /></div>
                <button disabled={busy} onClick={confirmBilling} className={primaryBtnCls} data-testid="lab-confirm-billing-btn">Create invoice &amp; confirm billing</button>
              </div>
            ) : <p className="text-sm text-slate-400">Front desk / billing staff will create the invoice.</p>}
          </section>
        )}

        {/* Billing */}
        {!pending && (
        <section className="panel p-4" data-testid="lab-detail-billing">
          <p className="overline text-[#EA580C] mb-3">Billing</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div><p className="text-slate-400 text-xs">Total</p><p className="font-semibold">₹{order.total}</p></div>
            <div><p className="text-slate-400 text-xs">Paid</p><p className="font-semibold text-emerald-600">₹{order.paid}</p></div>
            <div><p className="text-slate-400 text-xs">Due</p><p className="font-semibold text-rose-600" data-testid="lab-detail-due">₹{order.due}</p></div>
            <div><p className="text-slate-400 text-xs">Status</p><p className="font-semibold">{order.payment_status}</p></div>
          </div>
          {can("lab.billing.manage") && order.due > 0 && (
            <div className="flex flex-wrap items-end gap-3 mt-4">
              <select className={`${inputCls} max-w-[130px]`} value={pay.mode} onChange={(e) => setPay({ ...pay, mode: e.target.value })} data-testid="lab-pay-mode">
                {["cash", "card", "upi", "netbanking", "cheque", "other"].map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <input type="number" placeholder="Amount" className={`${inputCls} max-w-[130px]`} value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} data-testid="lab-pay-amount" />
              <input placeholder="Reference" className={`${inputCls} max-w-[150px]`} value={pay.reference} onChange={(e) => setPay({ ...pay, reference: e.target.value })} data-testid="lab-pay-ref" />
              <button disabled={busy} onClick={addPayment} className={primaryBtnCls} data-testid="lab-add-payment-btn">Record payment</button>
            </div>
          )}
        </section>
        )}

        {/* Sample */}
        {!pending && (
        <section className="panel p-4" data-testid="lab-detail-sample">
          <p className="overline text-[#EA580C] mb-3">Sample</p>
          {order.sample?.collected ? (
            <p className="text-sm text-emerald-700">Collected {order.sample.collected_at ? dmyTime(order.sample.collected_at) : ""} by {order.sample.collected_by}</p>
          ) : can("lab.sample.collect") ? (
            <button disabled={busy} onClick={collect} className={primaryBtnCls} data-testid="lab-collect-btn">Mark sample collected</button>
          ) : <p className="text-sm text-slate-400">Awaiting sample collection.</p>}
        </section>
        )}

        {/* Results */}
        {!pending && (
        <section className="panel p-4" data-testid="lab-detail-results">
          <p className="overline text-[#EA580C] mb-3">Results</p>
          <div className="space-y-3">
            {order.items.map((i) => (
              <div key={i.test_id} className="grid grid-cols-1 sm:grid-cols-6 gap-2 items-center" data-testid={`lab-result-row-${i.test_id}`}>
                <p className="text-sm font-medium text-slate-800 sm:col-span-2">{i.name}{i.verified && <span className="ml-2 text-xs text-emerald-600">✓ verified</span>}</p>
                <input className={inputCls} placeholder="Result" disabled={locked || !can("lab.results.enter")} value={results[i.test_id].result_value}
                  onChange={(e) => setResults({ ...results, [i.test_id]: { ...results[i.test_id], result_value: e.target.value } })} data-testid={`lab-result-value-${i.test_id}`} />
                <input className={inputCls} placeholder="Unit" disabled={locked || !can("lab.results.enter")} value={results[i.test_id].unit}
                  onChange={(e) => setResults({ ...results, [i.test_id]: { ...results[i.test_id], unit: e.target.value } })} />
                <input className={inputCls} placeholder="Ref range" disabled={locked || !can("lab.results.enter")} value={results[i.test_id].ref_range}
                  onChange={(e) => setResults({ ...results, [i.test_id]: { ...results[i.test_id], ref_range: e.target.value } })} />
                <input className={inputCls} placeholder="Flag (H/L)" disabled={locked || !can("lab.results.enter")} value={results[i.test_id].flag}
                  onChange={(e) => setResults({ ...results, [i.test_id]: { ...results[i.test_id], flag: e.target.value } })} />
              </div>
            ))}
          </div>
          {can("lab.results.enter") && !locked && (
            <div className="flex gap-3 mt-4">
              <button disabled={busy} onClick={() => saveResults(false)} className={ghostBtnCls} data-testid="lab-save-results-btn">Save draft</button>
              <button disabled={busy} onClick={() => saveResults(true)} className={primaryBtnCls} data-testid="lab-submit-results-btn">Submit for verification</button>
            </div>
          )}
          {can("lab.results.verify") && order.status === "completed" && (
            <button disabled={busy} onClick={verify} className={`${primaryBtnCls} mt-4`} data-testid="lab-verify-btn">Verify results</button>
          )}
          {can("lab.results.verify") && locked && (
            <button disabled={busy} onClick={() => act(() => api.post(`/lab/orders/${order.order_id}/reopen`), "Reopened for editing.")} className={`${ghostBtnCls} mt-4`} data-testid="lab-reopen-btn">Reopen</button>
          )}
        </section>
        )}

        {/* Report */}
        {!pending && (
        <section className="panel p-4" data-testid="lab-detail-report">
          <p className="overline text-[#EA580C] mb-3">Report</p>
          {order.report_released ? (
            <div className="flex items-center gap-3">
              <span className="text-sm text-emerald-700">Report released.</span>
              {can("lab.report.print") && <button onClick={printReport} className={primaryBtnCls} data-testid="lab-print-btn">Print / Download</button>}
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-sm text-slate-500">
                {order.status === "verified"
                  ? (order.due > 0 ? `Report is held — ₹${order.due} still due. Collect full payment to release.` : "Ready to release.")
                  : "Report can be released only after results are verified."}
              </p>
              {can("lab.report.release") && order.status === "verified" && (
                <button disabled={busy} onClick={release} className={primaryBtnCls} data-testid="lab-release-btn">Release report</button>
              )}
            </div>
          )}
        </section>
        )}
      </div>
    </Modal>
  );
}

function openPrint(order, business, meta = {}) {
  const rows = order.items.map((i) => `<tr>
    <td style="padding:6px 8px;border-bottom:1px solid #eee">${i.name}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #eee">${i.result_value || "-"} ${i.flag ? `<b>(${i.flag})</b>` : ""}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #eee">${i.unit || ""}</td>
    <td style="padding:6px 8px;border-bottom:1px solid #eee">${i.ref_range || ""}</td></tr>`).join("");
  const accent = business?.document?.accent_color || "#0f172a";
  const logo = business?.logo_data
    ? `<img src="${business.logo_data}" alt="logo" style="height:56px;object-fit:contain"/>` : "";
  const qr = meta?.qr_data_uri
    ? `<div style="text-align:center"><img src="${meta.qr_data_uri}" style="height:96px;width:96px"/>
       <div style="font-size:10px;color:#64748b;margin-top:4px">${meta.verification_code || ""}</div></div>` : "";
  const signatory = `
    <div style="margin-top:48px;text-align:right">
      <div style="display:inline-block;text-align:center;min-width:200px">
        <div style="border-top:1px solid #0f172a;padding-top:6px;font-weight:600">${meta.signatory_name || meta.report_released_by || ""}</div>
        <div style="font-size:12px;color:#64748b">${meta.signatory_designation || ""}</div>
        <div style="font-size:11px;color:#94a3b8">${meta.signature_label || "Authorised Signatory"}</div>
      </div>
    </div>`;
  const html = `<html><head><title>${order.order_no} — Lab Report</title></head>
  <body style="font-family:system-ui,Arial;padding:32px;color:#0f172a">
    <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:16px">
      <div style="display:flex;gap:14px;align-items:center">${logo}
        <div><h2 style="margin:0;color:${accent}">${business?.name || "Laboratory"}</h2>
        <p style="color:#64748b;margin:2px 0">${business?.address || ""}</p>
        <p style="color:#64748b;margin:0;font-size:12px">${business?.phone || ""}${business?.gstin ? " · GSTIN " + business.gstin : ""}</p></div>
      </div>
      ${qr}
    </div>
    <hr style="border:none;border-top:2px solid ${accent};margin:14px 0"/>
    <p><b>Order:</b> ${order.order_no} &nbsp; <b>Date:</b> ${new Date(order.created_at).toLocaleString()}</p>
    <p><b>Patient:</b> ${order.patient_name} (${order.patient_uhid}) &nbsp; <b>Age/Sex:</b> ${order.patient_age ?? "-"}/${order.patient_gender || "-"}</p>
    <p><b>Referred by:</b> ${order.doctor_name || "-"}</p>
    <table style="width:100%;border-collapse:collapse;margin-top:12px">
      <thead><tr style="text-align:left;background:#f8fafc">
        <th style="padding:6px 8px">Test</th><th style="padding:6px 8px">Result</th><th style="padding:6px 8px">Unit</th><th style="padding:6px 8px">Reference</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    ${signatory}
    <p style="margin-top:24px;color:#64748b;font-size:12px">Verified & released — ${meta.released_by || order.report_released_by || ""}. ${business?.document?.footer_text || "This is a laboratory report, not a government certificate."}</p>
  </body></html>`;
  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); w.focus(); w.print(); }
}
